// RIKSU'S OWN WAY IN — and the thing it must never grow is a form.
//
// # There is nothing here to type, and that is the constraint rather than the styling
//
// `AGENTS.md`: "No login, no credential storage and no redirect-URI registry lives here."
// Riksu is a relying party. It redeems a code, verifies a token against the issuer's
// JWKS, reads the tenant claim, and fails closed when no key is configured — it does not
// authenticate anybody and holds nothing anybody could authenticate with.
//
// So this screen has **no `<input>` at all**: no password field, no e-mail field, no
// `<form>`. It is the surface that STARTS a redirect and says where the person is going.
// A screen that merely looks like a login is the shape somebody wires a password into six
// months later, in a hurry, because the field was already there — which is why
// `sign-in.test.tsx` fails the build if a credential-shaped control ever appears here.
//
// # Two halves, one quiet action and one story
//
// LEFT is the product mark and the one control that begins the redirect. RIGHT is a
// supplemental product story around a decorative employment illustration. The RFC 9728
// read still decides whether a safe route exists, but implementation metadata is not made
// into customer-facing copy.
//
// # On a phone the action comes first, and it comes first in the DOM
//
// One column below `lg`, the action half first, the panel filling what is left. No
// `order-*` anywhere: the reading order, the tab order and the visual order are the same
// order, which is the only arrangement that cannot get out of step. A decorative half
// above the only button is a screen people scroll past.
//
// # A refusal is a SUMMARY and is never pinned to a control
//
// Riksu's `Error` is RFC 9457 problem details and has no per-field member, so there is
// nothing here to attach a message to even if there were a field. `ProblemNotice` renders
// it as a block with `role="alert"`. It deliberately does NOT take focus: `useFocusWhenShown`
// exists for a summary that appears in answer to a submit, and stealing focus on a page
// somebody has just arrived at moves them somewhere they did not ask to be.

import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import mitraMark from "../assets/mitra-logo.png";
import { useT } from "../shell/i18n";
import { ProblemNotice, problemOf } from "../shell/problem";
import { ActionLink } from "../ui/button";
import { type SignInState, signInHref } from "./session";
import { SignInFrame } from "./sign-in-frame";
import { askTheWayIn, type WayIn } from "./way-in";

/**
 * The address this screen owns.
 *
 * Signed OUT it renders at whatever address was asked for, because that address is where
 * the person is going back to. Signed IN it is reachable only here — somebody who typed
 * it, or followed an old link — and `app.tsx` routes it ABOVE the shell's `<Routes>`, so
 * it is deliberately not in `SCREENS`, not in `ROUTE_PATHS` and not in `destinations.ts`.
 * It is the door, not a place in the product, and it has no breadcrumb for the same
 * reason it has no rail.
 */
export const SIGN_IN_PATH = "/sign-in";

/**
 * Where the sign-in comes back to.
 *
 * The current address, including its query, so a filtered list survives the round trip.
 * Landing back on `/sign-in` would be a loop, so that one address answers with the root.
 * The BFF narrows this again on its side (`safe_return_to`) — an open redirect is a
 * phishing primitive and neither end trusts the other with it.
 */
export function returnToFor(here: { readonly pathname: string; readonly search: string }): string {
  return here.pathname === SIGN_IN_PATH ? "/" : `${here.pathname}${here.search}`;
}

export function SignIn({ state }: { readonly state: SignInState }) {
  const here = useLocation();
  const [wayIn, setWayIn] = useState<WayIn>({ status: "asking" });

  useEffect(() => {
    const abort = new AbortController();
    let current = true;
    askTheWayIn(abort.signal).then((answer) => {
      // The tab may be gone by the time this lands, and a `.then` after an abort still
      // runs. Same guard `App` uses on the session.
      if (current) {
        setWayIn(answer);
      }
    });
    return () => {
      current = false;
      abort.abort();
    };
  }, []);

  return (
    <SignInFrame>
      <WayForward state={state} wayIn={wayIn} here={here} />
    </SignInFrame>
  );
}

/**
 * The one thing to do next, whichever of the states this screen is in.
 *
 * Every arm is here rather than spread over the file because they are alternatives: a
 * reader is in exactly one of them, and a `switch` with no default is what makes adding a
 * `SignInState` a type error instead of a blank half-screen.
 */
function WayForward({
  state,
  wayIn,
  here,
}: {
  readonly state: SignInState;
  readonly wayIn: WayIn;
  readonly here: { readonly pathname: string; readonly search: string };
}) {
  const t = useT();

  switch (state.status) {
    case "loading":
      // NOT A SPINNER. One sentence, in a polite live region, because the wait is a
      // network round trip and the person has nothing to do during it.
      return (
        <p className="m-0 text-shell-body text-on-shell-muted" aria-live="polite">
          {t("app.checkingSession")}
        </p>
      );

    case "candidate":
      return (
        <ActionLink purpose="primary" render={<Link to="/onboarding/candidate" />}>
          {t("candidate.heading")}
        </ActionLink>
      );

    case "signed_in":
      // ARRIVED HERE BY TYPING THE ADDRESS. Saying so and offering the way onward,
      // rather than drawing a sign-in button for somebody who is already signed in —
      // which would send them round mitra to end up exactly where they started.
      return (
        <section className="flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-1">
            <h2 className="m-0 text-[length:var(--type-shell-surface-title-size)] leading-[var(--type-shell-surface-title-leading)] font-medium">
              {t("signIn.already.heading")}
            </h2>
            <p className="m-0 text-shell-body text-on-shell-muted">
              {t("signIn.already.as", {
                who: state.riksu.person?.display_name ?? state.riksu.subject,
                tenant: state.riksu.tenant_id,
              })}
            </p>
          </div>
          {/* A ROUTER LINK, unlike the sign-in below it: `/` is this application's own
              address, and a full page load to reach a screen already in memory is a
              whole bundle fetched to render what is one navigation away. */}
          <ActionLink purpose="primary" render={<Link to="/" viewTransition />}>
            {t("signIn.continue")}
          </ActionLink>
        </section>
      );

    case "failed":
      return (
        <section className="flex flex-col items-start gap-4">
          {/* THE SUMMARY, and it stands alone. `ProblemNotice` carries riksu's own
              sentence — its refusals name what WOULD have worked — under `role="alert"`,
              and nothing here attaches it to a control, because there is no control it
              could be about. */}
          <ProblemNotice failure={state.failure} whileDoing={t("signIn.whileSigningIn")} />
          {/* ONE WAY BACK, NOT TWO. `ProblemNotice` already renders a sign-in link for a
              401 — that class IS "you need to sign in", and its link is the answer to it.
              A second identical control beside it would be two things to press for one
              action, which is the defect the topbar's duplicated bell was. */}
          {problemOf(state.failure)?.status === 401 ? null : (
            <StartTheRedirect wayIn={wayIn} here={here} />
          )}
        </section>
      );

    case "signed_out":
      return <StartTheRedirect wayIn={wayIn} here={here} />;
  }
}

/**
 * The single control that begins the redirect — or the sentence saying why there is none.
 *
 * A LINK and not a button: `/auth/login` is the BFF's address, it is a full page
 * navigation out of this bundle, and a router link to it would render this application's
 * own 404 at an address this application does not own.
 */
function StartTheRedirect({
  wayIn,
  here,
}: {
  readonly wayIn: WayIn;
  readonly here: { readonly pathname: string; readonly search: string };
}) {
  const t = useT();

  // RIKSU SAID IT CANNOT ACCEPT ONE. The control is not rendered rather than disabled:
  // a grey button is a thing to try and keep trying, and `riksu_api::discovery` answers
  // this 503 for the express purpose of not sending anybody through an authorization
  // flow that must end in a refusal. Everything else — an unanswered probe, an offline
  // browser — leaves the door alone, because a probe must never be what hides the only
  // way into the product.
  if (wayIn.status === "closed") {
    return (
      <div
        role="alert"
        className="flex max-w-(--frame-prose) flex-col gap-1.5 rounded-lg border border-surface-negative-border bg-surface-negative-subtle px-4 py-3 text-left text-shell-body text-shell-primary"
      >
        <h2 className="m-0 text-[length:var(--type-shell-label-size)] font-medium text-destructive">
          {t("signIn.closed.heading")}
        </h2>
        <p className="m-0">{t("signIn.closed.body")}</p>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-center">
      <ActionLink
        purpose="identity"
        className="sign-in-action"
        render={<a href={signInHref(returnToFor(here))} />}
      >
        <img
          src={mitraMark}
          alt=""
          aria-hidden="true"
          className="size-7 object-contain brightness-0 invert"
        />
        <span>{t("signIn.withMitra")}</span>
      </ActionLink>
    </div>
  );
}
